import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import MenuBar from "../components/MenuBar";
import { RECORDS_PAGE_SIZE } from "../features/salesDataImport/constants";
import { canAccessSalesDataImport } from "../features/salesDataImport/access";
import SalesImportNav from "../features/salesDataImport/components/SalesImportNav";
import { useSalesImportBranchFilter } from "../features/salesDataImport/hooks/useSalesImportBranchFilter";
import {
  lineBillAmount,
  listUploadedWeekPeriods,
  loadWeeklyLineChunkRows,
  normalizeVoucherType,
} from "../features/salesDataImport/services/salesLineChunksReader";
import { rowMatchesDateRange } from "../features/salesDataImport/utils/dateParse";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { importRowSalespersonName, importRowStaffName } from "../features/salesDataImport/utils/importRowFields";

function fm(n) {
  return Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

/** One row per VoucherNo (lines often repeat the same voucher on every item). */
function aggregateUniqueVouchers(rows) {
  const map = new Map();
  for (const r of rows) {
    const voucherNo = String(r.voucherNo || "").trim() || "—";
    const bill = lineBillAmount(r);
    const prev = map.get(voucherNo);
    if (!prev) {
      map.set(voucherNo, {
        voucherNo,
        customerName: String(r.customerName || "").trim(),
        employeeName: importRowSalespersonName(r),
        staffName: importRowStaffName(r),
        billAmount: bill,
        lineCount: 1,
      });
      continue;
    }
    prev.lineCount += 1;
    if (!prev.customerName && r.customerName) prev.customerName = String(r.customerName).trim();
    if (!prev.employeeName) prev.employeeName = importRowSalespersonName(r);
    if (!prev.staffName && importRowStaffName(r)) prev.staffName = importRowStaffName(r);
    if (bill > prev.billAmount) prev.billAmount = bill;
  }
  return [...map.values()].sort((a, b) => a.voucherNo.localeCompare(b.voucherNo, undefined, { numeric: true }));
}

export default function SalesVoucherListPage() {
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}") || {};
    } catch {
      return {};
    }
  }, []);

  const { filterRows, branchId, branchFilterLabel } = useSalesImportBranchFilter();

  const [dark, setDark] = useState(() => localStorage.getItem("sdic-dark") === "1");
  const [uploadedWeeks, setUploadedWeeks] = useState([]);
  const [selectedPeriodKey, setSelectedPeriodKey] = useState("");
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [voucherType, setVoucherType] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");
  const [tablePage, setTablePage] = useState(0);

  const selectedWeekMeta = useMemo(
    () => uploadedWeeks.find((w) => w.periodKey === selectedPeriodKey) || null,
    [uploadedWeeks, selectedPeriodKey]
  );

  const branchScopedRows = useMemo(() => filterRows(allRows), [allRows, branchId, filterRows]);

  const voucherOptions = useMemo(() => {
    const set = new Set();
    for (const row of branchScopedRows) {
      set.add(normalizeVoucherType(row));
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [branchScopedRows]);

  const filteredLineRows = useMemo(() => {
    if (!voucherType) return [];
    const term = search.trim().toLowerCase();
    const hasDateFilter = Boolean(fromDate || toDate);
    return branchScopedRows.filter((r) => {
      if (normalizeVoucherType(r) !== voucherType) return false;
      if (hasDateFilter && !rowMatchesDateRange(r, fromDate, toDate)) return false;
      if (!term) return true;
      return (
        String(r.voucherNo || "").toLowerCase().includes(term) ||
        String(r.customerName || "").toLowerCase().includes(term) ||
        String(r.employeeName || "").toLowerCase().includes(term)
      );
    });
  }, [branchScopedRows, voucherType, fromDate, toDate, search]);

  const filteredRows = useMemo(() => aggregateUniqueVouchers(filteredLineRows), [filteredLineRows]);

  const totalAmount = useMemo(
    () => filteredRows.reduce((s, r) => s + r.billAmount, 0),
    [filteredRows]
  );

  const items = useMemo(() => {
    const start = tablePage * RECORDS_PAGE_SIZE;
    return filteredRows.slice(start, start + RECORDS_PAGE_SIZE);
  }, [filteredRows, tablePage]);

  const hasMoreTable = (tablePage + 1) * RECORDS_PAGE_SIZE < filteredRows.length;

  const refreshWeekList = async () => {
    const weeks = await listUploadedWeekPeriods();
    setUploadedWeeks(weeks);
    if (weeks.length && !weeks.some((w) => w.periodKey === selectedPeriodKey)) {
      setSelectedPeriodKey(weeks[0].periodKey);
    }
    return weeks;
  };

  const loadWeekRows = async (meta) => {
    if (!meta) {
      setAllRows([]);
      return;
    }
    setLoading(true);
    try {
      const rows = await loadWeeklyLineChunkRows(meta.year, meta.monthIndex, meta.weekOfMonth);
      setAllRows(rows);
      setTablePage(0);
    } catch (e) {
      toast.error(e?.message || "Failed to load week.");
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canAccessSalesDataImport(user)) return;
    refreshWeekList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!canAccessSalesDataImport(user)) return;
    loadWeekRows(selectedWeekMeta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWeekMeta?.periodKey]);

  useEffect(() => {
    setTablePage(0);
  }, [voucherType, fromDate, toDate, search, selectedPeriodKey, branchId]);

  useEffect(() => {
    if (voucherType && voucherOptions.length && !voucherOptions.includes(voucherType)) {
      setVoucherType("");
    }
  }, [voucherOptions, voucherType]);

  const exportExcel = () => {
    if (!filteredRows.length) return;
    const sheetRows = filteredRows.map((r) => ({
      VoucherNo: r.voucherNo,
      CustomerName: r.customerName,
      EmployeeName: r.employeeName,
      BillAmount: r.billAmount,
      LineCount: r.lineCount,
    }));
    const ws = XLSX.utils.json_to_sheet(sheetRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "VoucherList");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `voucher-list-${voucherType.replace(/\s+/g, "-")}.xlsx`
    );
  };

  if (!canAccessSalesDataImport(user)) {
    return <div className="p-6 text-center">Access denied — Admin or Storekeeper only.</div>;
  }

  return (
    <div className={`sdic-root${dark ? " sdic-dark" : ""}`}>
      <MenuBar />
      <header className="sdic-header">
        <div>
          <h1>Voucher Line List</h1>
          <p>One row per VoucherNo — Customer, Employee, Bill Amount (no duplicate voucher numbers)</p>
        </div>
        <button type="button" className="sdic-btn sdic-btn-secondary" onClick={() => setDark((d) => !d)}>
          {dark ? "Light" : "Dark"}
        </button>
      </header>

      <div className="sdic-body">
        <SalesImportNav active="voucher-list" />
        <p style={{ fontSize: "0.82rem", color: "var(--sdic-muted)", margin: "-0.35rem 0 0.75rem" }}>
          Branch filter: <strong>{branchFilterLabel}</strong>
        </p>

        <section className="sdic-panel">
          <h2 className="sdic-panel-title">Filters</h2>
          <div className="sdic-week-banner">
            <label>
              Uploaded week
              <select
                value={selectedPeriodKey}
                onChange={(e) => setSelectedPeriodKey(e.target.value)}
                disabled={loading}
                style={{ padding: "0.4rem", minWidth: "280px" }}
              >
                <option value="">— Select week —</option>
                {uploadedWeeks.map((w) => (
                  <option key={w.periodKey} value={w.periodKey}>
                    {w.periodKey} · {w.rowCount.toLocaleString()} lines
                  </option>
                ))}
              </select>
            </label>
            <label>
              Voucher Type <span style={{ color: "var(--sdic-danger)" }}>*</span>
              <select
                value={voucherType}
                onChange={(e) => setVoucherType(e.target.value)}
                disabled={loading || !allRows.length}
                style={{ padding: "0.4rem", minWidth: "220px" }}
              >
                <option value="">— Select voucher type —</option>
                {voucherOptions.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <label>
              TranDate From
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </label>
            <label>
              TranDate To
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </label>
          </div>
          {selectedWeekMeta && (
            <p style={{ fontSize: "0.85rem", color: "var(--sdic-muted)", margin: "0.5rem 0 0" }}>
              {loading
                ? "Loading lineChunks…"
                : `${allRows.length.toLocaleString()} line(s) in week · ${selectedWeekMeta.fromDate} → ${selectedWeekMeta.toDate}`}
            </p>
          )}
        </section>

        {voucherType && (
          <p style={{ fontSize: "0.85rem", color: "var(--sdic-muted)", margin: "0 0 0.75rem" }}>
            <strong>{filteredRows.length.toLocaleString()}</strong> voucher(s) for <strong>{voucherType}</strong>
            {filteredLineRows.length !== filteredRows.length && (
              <>
                {" "}
                ({filteredLineRows.length.toLocaleString()} item lines)
              </>
            )}
            {filteredRows.length > 0 && (
              <>
                {" "}
                · Total Bill Amount: <strong>₹ {fm(totalAmount)}</strong>
              </>
            )}
          </p>
        )}

        <div className="sdic-toolbar">
          <input
            className="sdic-search"
            placeholder="Search VoucherNo, Customer, Employee…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={!voucherType}
          />
          <button
            type="button"
            className="sdic-btn sdic-btn-primary"
            onClick={exportExcel}
            disabled={!filteredRows.length}
          >
            Export to Excel
          </button>
          <button
            type="button"
            className="sdic-btn sdic-btn-secondary"
            onClick={() => loadWeekRows(selectedWeekMeta)}
            disabled={loading || !selectedWeekMeta}
          >
            Refresh
          </button>
        </div>

        {!voucherType ? (
          <p style={{ color: "var(--sdic-muted)" }}>Select an uploaded week, then choose a voucher type to list unique vouchers.</p>
        ) : loading && !allRows.length ? (
          <p>Loading…</p>
        ) : (
          <div className="sdic-table-wrap">
            <table className="sdic-table">
              <thead>
                <tr>
                  <th>VoucherNo</th>
                  <th>CustomerName</th>
                  <th>EmployeeName</th>
                  <th>Bill Amount</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.voucherNo}>
                    <td>{r.voucherNo}</td>
                    <td>{r.customerName || "—"}</td>
                    <td>{r.employeeName || "—"}</td>
                    <td>₹ {fm(r.billAmount)}</td>
                  </tr>
                ))}
                {!items.length && (
                  <tr>
                    <td colSpan={4} style={{ color: "var(--sdic-muted)" }}>
                      No vouchers for this type{fromDate || toDate ? " in selected TranDate range" : ""}.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {hasMoreTable && (
          <button
            type="button"
            className="sdic-btn sdic-btn-secondary"
            style={{ marginTop: "1rem" }}
            onClick={() => setTablePage((p) => p + 1)}
          >
            Show more ({items.length} / {filteredRows.length.toLocaleString()})
          </button>
        )}
      </div>
    </div>
  );
}
